import type { ReactNode } from "react"

export type ConfirmTone = "default" | "danger"

export type ConfirmOptions = {
  title?: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  /** When set, dialog asks for the current user's password and confirmWithPassword returns it. */
  requirePassword?: boolean
  passwordLabel?: string
}

export type ConfirmChoice = "confirm" | "alternative"

/** Two actions instead of action + cancel; Escape, the close button and a click outside resolve to null. */
export type ConfirmChoiceOptions = Omit<ConfirmOptions, "cancelLabel" | "requirePassword" | "passwordLabel"> & {
  alternativeLabel: string
  alternativeTone?: ConfirmTone
}

export type ConfirmRequest = {
  title?: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone: ConfirmTone
  requirePassword: boolean
  passwordLabel?: string
  alternativeLabel?: string
  alternativeTone?: ConfirmTone
}

type ConfirmOpener = (request: ConfirmRequest) => Promise<boolean>
type PasswordConfirmOpener = (request: ConfirmRequest) => Promise<string | null>
type ChoiceConfirmOpener = (request: ConfirmRequest) => Promise<ConfirmChoice | null>

let opener: ConfirmOpener | null = null
let passwordOpener: PasswordConfirmOpener | null = null
let choiceOpener: ChoiceConfirmOpener | null = null

export function registerConfirmOpener(next: ConfirmOpener | null) {
  opener = next
}

export function registerPasswordConfirmOpener(next: PasswordConfirmOpener | null) {
  passwordOpener = next
}

export function registerChoiceConfirmOpener(next: ChoiceConfirmOpener | null) {
  choiceOpener = next
}

function normalize(input: string | ConfirmOptions, requirePassword = false): ConfirmRequest {
  if (typeof input === "string") {
    return { message: input, tone: "default", requirePassword }
  }
  return {
    title: input.title,
    message: input.message,
    confirmLabel: input.confirmLabel,
    cancelLabel: input.cancelLabel,
    tone: input.tone ?? "default",
    requirePassword: requirePassword || Boolean(input.requirePassword),
    passwordLabel: input.passwordLabel,
  }
}

/** Promise-based confirm dialog. Requires ConfirmHost mounted in a shell layout. */
export function confirm(message: string): Promise<boolean>
export function confirm(options: ConfirmOptions): Promise<boolean>
export function confirm(input: string | ConfirmOptions): Promise<boolean> {
  if (!opener) {
    throw new Error("ConfirmHost is not mounted. Call confirm() only after app shells render.")
  }
  return opener(normalize(input, false))
}

export function confirmChoice(options: ConfirmChoiceOptions): Promise<ConfirmChoice | null> {
  if (!choiceOpener) {
    throw new Error("ConfirmHost is not mounted. Call confirmChoice() only after app shells render.")
  }
  return choiceOpener({
    ...normalize(options),
    alternativeLabel: options.alternativeLabel,
    alternativeTone: options.alternativeTone ?? "default",
  })
}

/**
 * Confirm dialog with a password field. Resolves to the entered password, or null if cancelled.
 * Server must verify via current_user.authenticate — never trust the client alone.
 */
export function confirmWithPassword(message: string): Promise<string | null>
export function confirmWithPassword(options: ConfirmOptions): Promise<string | null>
export function confirmWithPassword(input: string | ConfirmOptions): Promise<string | null> {
  if (!passwordOpener) {
    throw new Error("ConfirmHost is not mounted. Call confirmWithPassword() only after app shells render.")
  }
  return passwordOpener(normalize(input, true))
}
